import { Dialog, DialogPanel, DialogTitle } from '@headlessui/react';
import React, { useState, useEffect, useRef } from 'react';
import { X, Search, Loader2, Star, TrendingUp, Check, ChevronRight } from 'lucide-react';
import { searchGames, getPopularGames } from '../services/boardGameService';
import { BoardGame } from '../types/boardgame';
import ErrorMessage from './ErrorMessage';

interface GameSearchModalProps {
  onClose: () => void;
  onGameSelect: (games: BoardGame[]) => void;
}

function GameSearchModal({ onClose, onGameSelect }: GameSearchModalProps) {
  const [searchQuery, setSearchQuery] = useState('');
  const [results, setResults] = useState<(BoardGame & { pageId: string })[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedGames, setSelectedGames] = useState<Map<string, BoardGame>>(new Map());

  const [error, setError] = useState<{message: string; code?: string} | null>(null);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [retryKey, setRetryKey] = useState(0);
  const requestId = useRef(0);

  useEffect(() => {
    const id = ++requestId.current;
    const query = searchQuery.trim();
    let cancelled = false;
    const isCurrent = () => !cancelled && requestId.current === id;
    setError(null);
    if (page === 1) {
      setResults([]);
      setHasMore(false);
    }
    setLoading(page === 1 && query.length !== 1);
    setLoadingMore(page > 1);

    // One request owns the visible state. Old popular/search responses cannot
    // overwrite a newer query, including while the new query is debouncing.
    const timer = window.setTimeout(async () => {
      if (query.length === 1) return;
      try {
        const response = query
          ? await searchGames(query, page)
          : { items: await getPopularGames(), hasMore: false };
        if (!isCurrent()) return;
        const games = response.items.map(game => ({ ...game, pageId: game.id }));
        setResults(previous => page === 1 ? games :
          Array.from(new Map([...previous, ...games].map(game => [game.id, game])).values()));
        setHasMore(response.hasMore);
      } catch (err: unknown) {
        if (!isCurrent()) return;
        const failure = err as { message?: string; code?: string };
        setError({
          message: failure.message || 'Unable to load games. Please try again.',
          code: failure.code
        });
      } finally {
        if (isCurrent()) {
          setLoading(false);
          setLoadingMore(false);
        }
      }
    }, query ? 300 : 0);
    return () => { cancelled = true; window.clearTimeout(timer); };
  }, [searchQuery, page, retryKey]);

  const changeQuery = (value: string) => {
    ++requestId.current;
    setSearchQuery(value);
    setPage(1);
  };

  const handleGameSelect = (game: BoardGame) => {
    setSelectedGames(prev => {
      const next = new Map(prev);
      if (next.has(game.id)) {
        next.delete(game.id);
      } else {
        next.set(game.id, game);
      }
      return next;
    });
  };

  const handleDone = () => {
    if (selectedGames.size > 0) {
      onGameSelect(Array.from(selectedGames.values()));
      onClose();
    }
  };

  const formatRating = (rating: number) => {
    return rating ? rating.toFixed(1) : 'N/A';
  };

  return (
    <Dialog open onClose={onClose} className="relative z-50">
      <div className="fixed inset-0 bg-black/50" aria-hidden="true" />
      <div className="fixed inset-0 overflow-y-auto p-4">
      <DialogPanel className="bg-white rounded-lg w-full max-w-md my-8 mx-auto min-w-0">
        <div className="flex items-center justify-between p-4 border-b">
          <div className="flex items-center gap-2">
            <DialogTitle className="text-lg font-semibold">Search Games</DialogTitle>
            {!searchQuery && !loading && (
              <div className="flex items-center gap-1 text-xs text-indigo-600 bg-indigo-50 px-2 py-1 rounded">
                <TrendingUp className="h-3 w-3" />
                <span>Popular Games</span>
              </div>
            )}
          </div>
          <div className="flex items-center gap-4">
            <button
              onClick={handleDone}
              disabled={selectedGames.size === 0}
              className={`px-4 py-2 rounded-lg transition ${
                selectedGames.size > 0
                  ? 'bg-indigo-600 text-white hover:bg-indigo-700'
                  : 'bg-gray-100 text-gray-400 cursor-not-allowed'
              }`}
            >
              Done ({selectedGames.size})
            </button>
            <button onClick={onClose} aria-label="Close game search" className="p-2 hover:bg-gray-100 rounded-full">
              <X className="h-5 w-5" />
            </button>
          </div>
        </div>

        <div className="p-4">
          <div className="relative mb-4">
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => changeQuery(e.target.value)}
              aria-label="Search games"
              placeholder="Enter game name..."
              className="w-full pl-4 pr-12 py-2 border rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
              autoFocus
            />
            {loading && (
              <div className="absolute right-2 top-1/2 -translate-y-1/2 p-1.5">
                <Loader2 className="h-5 w-5 animate-spin text-gray-400" />
              </div>
            )}
          </div>

          {error && (
            <div role="alert" className="space-y-3 mb-4">
              <ErrorMessage message={error.message} code={error.code} />
              <button onClick={() => setRetryKey(key => key + 1)} className="px-4 py-2 border rounded-lg">
                Try again
              </button>
            </div>
          )}

          <p role="status" className="text-sm text-gray-600 mb-4">
            {loading ? 'Loading games…' : loadingMore ? 'Loading more games…' :
              searchQuery.trim().length === 1 ? 'Enter at least 2 characters to search.' :
              !error && results.length === 0 ? (searchQuery.trim()
                ? 'No games found. Try a different name.'
                : 'No popular games available. Search for a game by name.') : ''}
          </p>
          <div className="space-y-4">
            {results.map((game) => (
              <button
                key={game.pageId}
                onClick={() => handleGameSelect(game)}
                className="w-full bg-white border rounded-lg p-4 flex items-center gap-4 hover:bg-gray-50 transition text-left"
              >
                <img
                  src={game.thumb_url}
                  alt={game.name}
                  className="w-16 h-16 object-cover rounded"
                  onError={(e) => {
                    e.currentTarget.src = '/board-game-placeholder.png';
                  }}
                />
                <div className="flex-1 min-w-0">
                  <h3 className="font-semibold truncate">{game.name}</h3>
                  <p className="text-sm text-gray-600">
                    {game.year_published} · {game.min_players}-{game.max_players} Players
                  </p>
                  <div className="flex items-center gap-4 mt-1">
                    {game.rank > 0 && (
                      <p className="text-xs text-indigo-600">
                        BGG Rank: #{game.rank}
                      </p>
                    )}
                    {game.average_user_rating > 0 && (
                      <div className="flex items-center gap-1 text-xs text-yellow-600">
                        <Star className="h-3 w-3 fill-yellow-500" />
                        <span>{formatRating(game.average_user_rating)}</span>
                      </div>
                    )}
                  </div>
                </div>
                {selectedGames.has(game.id) ? (
                  <Check className="h-5 w-5 text-green-500" />
                ) : (
                  <ChevronRight className="h-5 w-5 text-gray-400" />
                )}
              </button>
            ))}

            {hasMore && !loading && !error && (
              <button
                onClick={() => setPage(current => current + 1)}
                disabled={loadingMore}
                className="w-full py-3 text-indigo-600 hover:bg-indigo-50 rounded-lg transition"
              >
                {loadingMore ? (
                  <div className="flex items-center justify-center gap-2">
                    <Loader2 className="h-4 w-4 animate-spin" />
                    <span>Loading more...</span>
                  </div>
                ) : (
                  'Load more results'
                )}
              </button>
            )}
          </div>
        </div>
      </DialogPanel>
      </div>
    </Dialog>
  );
}

export default GameSearchModal;
